import type { SxProps, Theme } from "@mui/material/styles";

import { CardSize } from "pages/shared/Dashboard/types";

export const MY_INVESTIGATIONS_TAB_KEY = "my-investigations";

export const DASHBOARD_EVENT_FILTERS_LOCALSTORAGE_KEY =
  "dashboard-event-filters-localstorage-key";
export const DASHBOARD_VEHICLE_FILTERS_LOCALSTORAGE_KEY =
  "dashboard-vehicle-filters-localstorage-key";

export const DASHBOARD_EVENT_TYPE_LOCALSTORAGE_KEY =
  "dashboard-event-type-localstorage-key";

export const FRC_VEHICLE_FILTER_ATTRIBUTE = "vehicle.vehicleManufacturedAt";
export const ALL_TIME_VEHICLE_FILTER_DATE_RANGE = "3m";

export const GRID_SPACING = 3;
const MUI_SPACING_UNIT = 8;
const GRID_GAP = GRID_SPACING * MUI_SPACING_UNIT;

// converts a column count (out of 12) to gap-aware flex-basis + max-width styles
// formula: calc(fraction * 100% - (1 - fraction) * gap)
const cols = (n: number) => {
  const fraction = n / 12;
  const pct = fraction * 100;
  const gapOffset = (1 - fraction) * GRID_GAP;
  const value = gapOffset === 0 ? `${pct}%` : `calc(${pct}% - ${gapOffset}px)`;

  return { flexBasis: value, maxWidth: value };
};

// horizontal space consumed by `.MuiCard-root` styling on each side: p-4 (16px) + 1px border
const CARD_INNER_HORIZONTAL_PADDING_PX = 17;

// container-query analog of `cols` that targets a sibling card's *content area*
// expressed in `cqw` so it resolves against the `cards` container query
const colsCQContent = (columnSpan: number) => {
  const columnFraction = columnSpan / 12;
  const widthPercent = columnFraction * 100;
  // gap-aware reduction: each sibling card gives up its proportional share of
  // the inter-card gap so the sum of siblings + gaps equals the container width
  const gapOffsetPx = (1 - columnFraction) * GRID_GAP;
  // additional pixel reduction so the result matches the card's *content area*
  // (inside its padding+border) instead of its outer box
  const totalOffsetPx = gapOffsetPx + 2 * CARD_INNER_HORIZONTAL_PADDING_PX;

  return {
    width: `calc(${widthPercent}cqw - ${totalOffsetPx}px)`,
    flexShrink: 0,
  };
};

// container-query breakpoints (based on actual content width, not viewport)
const cq = (minWidth: number) => `@container cards (min-width: ${minWidth}px)`;
const SM = cq(600);
const MD = cq(900);
const LG = cq(1200);
const XL = cq(1536);

// responsive card sizes using container queries so they adapt to the
// actual available width (accounting for sidebar, duck window, etc.)
export const CARD_SIZE_STYLES: Record<CardSize, SxProps<Theme>> = {
  small: {
    flexGrow: 0,
    ...cols(6),
    [SM]: cols(4),
    [MD]: cols(2.7),
    [LG]: cols(2),
    [XL]: cols(1),
  },
  medium: {
    flexGrow: 0,
    ...cols(12),
    [SM]: cols(8),
    [MD]: cols(5.4),
    [LG]: cols(4),
    [XL]: cols(3),
  },
  large: {
    flexGrow: 0,
    ...cols(12),
    [SM]: cols(12),
    [MD]: cols(8),
    [LG]: cols(6),
    [XL]: cols(6),
  },
  full: {
    flexGrow: 0,
    ...cols(12),
    [SM]: cols(12),
    [MD]: cols(12),
    [LG]: cols(12),
    [XL]: cols(12),
  },
};

// width styles mirroring the `small` entry of `CARD_SIZE_STYLES`.
// the resulting width equals the *content area* of a sibling small card
export const SMALL_CARD_CONTENT_WIDTH_STYLES: SxProps<Theme> = {
  ...colsCQContent(6),
  [SM]: colsCQContent(4),
  [MD]: colsCQContent(2.7),
  [LG]: colsCQContent(2),
  [XL]: colsCQContent(1),
};
